import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GTMTracking } from '@common/ui/general';
import {
  requestParamsSelector,
} from '@alumni_v2/selectors/reviews';
import {
  updateRequestParams,
} from '@alumni_v2/stores/reviews/actions';

import searchIcon from '@alumni_v2/images/svg/searchIcon.svg';

import styles from './SearchBar.module.scss';

const availableQuickSearch = ['Infosys', 'Tcs', 'Amazon', 'Wipro', 'Cognizant'];

const SearchBar = () => {
  const dispatch = useDispatch();

  const { quickFilters } = window.__INITIAL_STATE__;
  const [showQuickSearchOptions, setShowQuickSearchOptions] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(new Set());
  const searchInputRef = useRef(null);
  const quickSearchOptionsRef = useRef(null);

  const handleSearchInputChange = (e) => {
    setSearchInputValue(e.target.value);
  };

  const handleSearchBarFocus = () => {
    setShowQuickSearchOptions(true);
  };

  const toggleSetElement = useCallback((set, element) => {
    if (set.has(element)) {
      set.delete(element);
    } else {
      set.add(element);
    }
  }, []);

  const handleQuickSearchOptionClick = useCallback((option) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = new Set(prevSelectedOptions);
      toggleSetElement(updatedOptions, option);

      // Update the request params with selected options
      dispatch(
        updateRequestParams(
          Array.from(updatedOptions), {}, null,
        ),
      );

      setShowQuickSearchOptions(false); // Close the quick search options

      return updatedOptions;
    });
  }, [dispatch, toggleSetElement]);

  const handleOutsideClick = useCallback(
    (e) => {
      if (
        searchInputRef.current
          && !searchInputRef.current.contains(e.target)
          && quickSearchOptionsRef.current
          && !quickSearchOptionsRef.current.contains(e.target)
      ) {
        setShowQuickSearchOptions(false);
      }
    },
    [],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const requestParams = useSelector(requestParamsSelector) || {};

  useEffect(() => {
    if (
      Object.keys(requestParams.advancedFilters).length
    ) {
      setSearchInputValue('');
      setSelectedOptions(new Set([]));
    }
    if (!requestParams.search && requestParams.quickFilters.length === 0) {
      setSearchInputValue('');
    }
  }, [requestParams, quickFilters]);

  useEffect(() => {
    const selectedFilterValues = quickFilters
      .filter(filterItem => requestParams.quickFilters.includes(
        filterItem.value,
      ))
      .map(filterItem => filterItem.value);
    setSelectedOptions(new Set(selectedFilterValues));
  }, [quickFilters, requestParams.quickFilters]);

  const submitSearchFilter = useCallback(() => {
    dispatch(
      updateRequestParams(
        [], {}, searchInputValue.trim(),
      ),
    );
    setSelectedOptions(new Set([]));
  }, [dispatch, searchInputValue]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const trimmedSearchInputValue = searchInputValue.trim();

      if (trimmedSearchInputValue) {
        submitSearchFilter();
      }
      setShowQuickSearchOptions(false);
    }, [
      submitSearchFilter, searchInputValue,
    ],
  );

  return (
    <div className={styles.searchBar}>
      <GTMTracking
        component="div"
        eventCategory="search"
        gtmEventType="cta"
        gtmEventAction="click"
        gtmEventResult="search"
        gtmEventCategory="alumni_directory"
        className={styles.searchContainer}
        gtmEventData={{
          click_type: 'search',
        }}
      >
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          <input
            className={styles.inputContainer}
            placeholder="Search by college, city or company"
            onFocus={handleSearchBarFocus}
            onChange={handleSearchInputChange}
            value={searchInputValue}
            ref={searchInputRef}
          />
          <GTMTracking
            component="button"
            gtmEventType="cta"
            gtmEventAction="click"
            gtmEventResult="search_submit"
            gtmEventCategory="alumni_directory"
            onClick={(e) => e.stopPropagation()}
            className={styles.searchBtn}
            gtmEventData={{
              click_type: 'search_submit',
              click_text: searchInputValue,
            }}
          >
            <img src={searchIcon} alt="searchIcon" />
          </GTMTracking>
        </form>
      </GTMTracking>
      {showQuickSearchOptions && (
        <div className={styles.quickSearchOptions} ref={quickSearchOptionsRef}>
          <div className={styles.quickSearchHeading}>Quick Searches</div>
          <div className={styles.quickSearchButtons}>
            {quickFilters.filter(
              filterItem => availableQuickSearch.includes(filterItem.label),
            ).map((filterItem, index) => {
              const isSelected = selectedOptions.has(filterItem.value);
              const buttonClassName = isSelected
                ? `${styles.quickSearchBtn} ${styles.quickSearchBtnSelected}`
                : styles.quickSearchBtn;
              return (
                <GTMTracking
                  component="button"
                  onClick={() => handleQuickSearchOptionClick(filterItem.value)}
                  className={buttonClassName}
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="quick_search_option"
                  gtmEventCategory="alumni_directory"
                  key={index}
                  gtmEventData={{
                    click_type: 'quick_search_option',
                    click_text: filterItem.label,
                  }}
                >
                  {filterItem.label}
                </GTMTracking>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
